import React, { useEffect, useRef, useState } from "react";
import InputMask from "react-input-mask";
import { useHistory, useParams } from "react-router-dom";
import loginBanner from "../../assets/images/login/WDTSbarrels.jpg";
import { Service } from "../../Service/ApiService";
import { CommonService } from "../../Service/CommonService";
import Spinner from "../Common/Spinner";
import "../Static/Login/Login.scss";
import "../Dynamic/AddCompanyOrUser.scss";
import useFormValidation from "../Dynamic/FormValidation";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import Logo from "../Logo/Logo";
import UATbelt from "./UATbelt";
import LoginFooter from "./LoginFooter";
import SignupSuccessPopup from "../popups/SignupSuccessPopup";
import { CookieService } from "../../Service/CookieService";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useZipcodeValidation } from "../../hooks/useZipcodeValidation";

const Signup = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [CompaniesList, setCompaniesList] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [showPasswordFields, setShowPasswordFields] = useState("no");
  const [selected, setSelected] = useState("No");
  const [showPassword, setShowPassword] = useState(false);
  const [showRetypePassword, setShowRetypePassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [isValidZipcode, setValidZipcode] = useState(false);
  const [isZipcodeLoading, setZipcodeLoading] = useState(false);
  const [isEmailExist, setIsEmailExist] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const [isShowSignupSuccessPopup, setShowSignupSuccessPopup] = useState(false);
  const [isShowErrorPopup, setShowErrorPopup] = useState(false);
  const [popupType, setPopupType] = useState("");
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    birthdate: "",
    zipcode: "",
    phoneNumber: "",
    email: "",
    streetAddress: "",
    userStatus: "PENDING_REVIEW",
    isBroker: false,
    isCompanyAccount: false,
    city: "",
    state: "",
    companyName: "",
  });

  const [errors, setErrors] = useState({
    firstName: "",
    birthdate: "",
    zipcode: "",
    email: "",
    companyName: "",
  });

  const { zipcodeLoading, validZipcode, validatePostalCode } = useZipcodeValidation(setFormData, setErrors);

  const redirectToLogin = () => {
    history.push("/login");
  }

  const redirectToMarketPlace = () => {
    history.push("/user/marketplace");
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    // Remove the error message for the field being changed
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    // Convert "true"/"false" string to boolean for isCompanyAccount
    const newValue = name === "isCompanyAccount" ? value === "true" : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const validateZipcode = async (zipcode: string): Promise<boolean> => {
    return await validatePostalCode(zipcode);
  }

  const handleZipcodeBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    setZipcodeLoading(true);
    const zipcode = e.target.value;
    await validateZipcode(zipcode);
  };

  const routeBack = () => {
    history.goBack();
  };

  const validateUserFields = async (): Promise<boolean> => {
    let isValidationPass = true;

    const requiredFields = [
      { field: "firstName", error: "Please enter first name" },
      { field: "birthdate", error: "Please enter birthdate" },
      { field: "email", error: "Please enter email" },
    ];

    // Validate required fields
    requiredFields.forEach(({ field, error }) => {
      const value = formData[field]?.trim?.() ?? "";
      if (value.length === 0 || errors[field]) {
        if (value.length === 0) {
          setErrors((prevState) => ({ ...prevState, [field]: error }));
        }
        isValidationPass = false;
      }
    });

    // Validate zipcode
    if (formData.zipcode.trim().length === 0 || errors.zipcode) {
      if (formData.zipcode.trim().length === 0) {
        setErrors((prevState) => ({
          ...prevState,
          zipcode: "Please enter zipcode",
        }));
      }
      isValidationPass = false;
    } else {
      const result = await validateZipcode(formData.zipcode.trim());
      if (!result) {
        isValidationPass = false;
      }
    }

    // Validate company name if isCompanyAccount is true
    if (formData.isCompanyAccount) {
      if (formData.companyName.trim().length === 0) {
        setErrors((prevState) => ({
          ...prevState,
          companyName: "Please enter account owner name",
        }));
        isValidationPass = false;
      }
    }

    return isValidationPass;
  };

  const dateInputRef = useRef(null);

  const handleDateClick = () => {
    if (dateInputRef.current) {
      dateInputRef.current?.showPicker(); // This will trigger the date picker to open
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoading(true);
    const isFormValid = await validateUserFields();

    if (!isFormValid) {
      setIsLoading(false);
      return;
    }

    try {

       if (!executeRecaptcha) {
        throw new Error("reCAPTCHA is not ready");
      }

      const token = await executeRecaptcha("signup");
      if (!token) {
        throw new Error("Failed to get reCAPTCHA token");
      }

      // Attach token to form data
      const payload = {
        ...formData,
        recaptchaToken: token,
      };

      let response = await Service.signUpUser(payload);

      //set popup type as REQUESTED
      setPopupType("REQUESTED");

      setShowSignupSuccessPopup(true);

      //also set signup status in local storage as REQUESTED
      CookieService.setSignupStatus("REQUESTED");
    } catch (error: any) {
      //now check error message comes or not
      if (
        error?.response?.data?.errorMessage == "PENDING_REVIEW" ||
        error?.response?.data?.errorMessage == "REJECTED"
      ) {
        setPopupType(error.response.data?.errorMessage);
        setShowSignupSuccessPopup(true);
      }

      if (error?.response?.data?.errorMessage == "Account Owner email already exists" || error?.response?.data?.errorMessage == "ACCEPTED") {
        CommonService.Toast.fire({
          title: "This email already exists. Please try another one.",
          icon: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getMinAgeDate = (): string => {
    const today = new Date();
    const minAgeYear = today.getFullYear() - 21;
    return `${minAgeYear}-${String(today.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(today.getDate()).padStart(2, "0")}`;
  };

  const minAgeDate = getMinAgeDate();

  return (
    <>
      <UATbelt />
      <div className="login text-start d-flex flex-column">
        <div className="mx-auto d-flex align-items-center flex-column">
          <div className="login__form">
            <div className="row d-flex align-items-center m-0">
              <div className="col-lg-12 px-0">
                <div className="login__form--outer signup-form-outer">
                  <div className="row mb-3vh mx-0">
                    <div className="col-12 px-0 text-center">
                      <a target="_blank" href="https://whitedogllc.com" className="d-inline-block">
                        <Logo size="auto" />
                      </a>
                      <p className="logo-sub__text mb-0 mt-2">
                        Presented by{" "}
                        <a
                          target="_blank"
                          href="https://whitedogllc.com"
                          className="ms-1"
                        >
                          White Dog Trading & Storage
                        </a>
                      </p>
                    </div>
                  </div>
                  <button onClick={redirectToMarketPlace} className="back-btn btn d-flex align-items-center gap-2 mb-2"><span className="arrow-wrapper"><ArrowBackIcon/></span><span className="back-btn-text"> Back to Marketplace</span></button>
                  <h2 className="form-heading">Create your account!</h2>
                  <p className="login__text">Join to browse listings and manage your containers.</p>
                  <div className="add-company-user-form-wrapper p-0">
                    <form
                      className="form__inner row d-flex pt-3"
                      onSubmit={onSubmit}
                      noValidate
                      onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                    >
                      {isEmailExist && showAlert && (
                        <div className="col-12">
                          <div
                            className="alert barrel-alert user-exist-alert alert-dismissible fade show"
                            role="alert"
                          >
                            <span className="d-flex align-items-center gap-2">
                              <WarningIcon />
                              Email already exists.
                            </span>
                            <button
                              type="button"
                              className="btn-close"
                              aria-label="Close"
                              onClick={() => setShowAlert(false)}
                            >
                              {" "}
                              <CloseIcon />
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="col-12 col-sm-6 col-lg-4 mb-3 login-form-group">
                        <label htmlFor="firstName" className="form-label">
                          First Name{" "}
                          <span className="star-required text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          placeholder="Enter first name"
                          value={formData.firstName}
                          onChange={(e) => {
                            const value = e.target.value.replace(
                              /[^A-Za-z ]/g,
                              ""
                            );
                            handleChange({
                              target: {
                                name: e.target.name,
                                value: value,
                              },
                            });
                          }}
                          className={`form-control ${
                            errors.firstName ? "is_invalid" : ""
                          }`}
                        />
                        {errors.firstName && (
                          <div className="error-message">
                            {errors.firstName}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-sm-6 col-lg-4 mb-3 login-form-group">
                        <label htmlFor="lastName" className="form-label">
                          Last Name
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          placeholder="Enter last name"
                          name="lastName"
                          value={formData.lastName}
                          onChange={(e) => {
                            const value = e.target.value.replace(
                              /[^A-Za-z ]/g,
                              ""
                            );
                            handleChange({
                              target: {
                                name: e.target.name,
                                value: value,
                              },
                            });
                          }}
                          className="form-control"
                        />
                      </div>
                      <div className="col-12 col-sm-6 col-lg-4 mb-3 login-form-group">
                        <label htmlFor="birthdate" className="form-label">
                          Birthdate (Must be 21+ years){" "}
                          <span className="star-required text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          id="birthdate"
                          name="birthdate"
                          placeholder="Birthdate"
                          value={formData.birthdate}
                          onChange={handleChange}
                          max={minAgeDate}
                          // ref={dateInputRef}
                          onClick={handleDateClick}
                          className={`form-control ${
                            errors.birthdate ? "is_invalid" : ""
                          }`}
                        />
                        {errors.birthdate && (
                          <div className="error-message">
                            {errors.birthdate}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-sm-6 col-lg-4 mb-3 login-form-group">
                        <label htmlFor="zipcode" className="form-label">
                          Zip Code{" "}
                          <span className="star-required text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="zipcode"
                          name="zipcode"
                          placeholder="Enter zipcode"
                          value={formData.zipcode}
                          onChange={handleChange}
                          onBlur={handleZipcodeBlur}
                          className={`form-control ${
                            errors.zipcode ? "is_invalid" : ""
                          }`}
                        />
                        {errors.zipcode && (
                          <div className="error-message">{errors.zipcode}</div>
                        )}
                        {formData.city && formData.state && (
                          <p className="mt-1 mb-0 zip-code" style={{fontSize: '12px'}}>
                            {formData.city + ", " + formData.state}
                          </p>
                        )}
                      </div>

                      <div className="col-12 col-sm-6 col-lg-4 mb-3 login-form-group">
                        <label htmlFor="email" className="form-label">
                          Email Address{" "}
                          <span className="star-required text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          placeholder="Enter email ID"
                          value={formData.email}
                          onChange={handleChange}
                          className={`form-control ${
                            errors.email ? "is_invalid" : ""
                          }`}
                        />
                        {errors.email && (
                          <div className="error-message">{errors.email}</div>
                        )}
                      </div>
                      <div className="col-12 col-sm-6 col-lg-4 mb-3 login-form-group">
                        <label htmlFor="phoneNumber" className="form-label">
                          Phone Number
                        </label>
                        <InputMask
                          mask="(999) 999-9999"
                          value={formData.phoneNumber}
                          onChange={handleChange}
                        >
                          {(inputProps) => (
                            <input
                              type="text"
                              id="phoneNumber"
                              name="phoneNumber"
                              placeholder="Enter phone number"
                              className="form-control"
                              {...inputProps}
                            />
                          )}
                        </InputMask>
                      </div>

                      <div className="col-12 col-sm-6 col-lg-4 mb-3 login-form-group">
                        <label htmlFor="streetAddress" className="form-label">
                          Street Address
                        </label>
                        <input
                          type="text"
                          id="streetAddress"
                          name="streetAddress"
                          placeholder="Enter street address"
                          value={formData.streetAddress}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>

                      <div className="col-12 col-sm-6 col-lg-4 mb-3 login-form-group">
                        <label className="form-label d-block">
                          Is Account Owner?
                        </label>
                        <div className="form-check form-check-inline custom-radio">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="isCompanyAccount"
                            id="isCompanyYes"
                            value="true"
                            checked={formData.isCompanyAccount}
                            onChange={handleChange}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="isCompanyYes"
                          >
                            Yes
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="isCompanyAccount"
                            id="isCompanyNo"
                            value="false"
                            checked={!formData.isCompanyAccount}
                            onChange={handleChange}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="isCompanyNo"
                          >
                            No
                          </label>
                        </div>
                      </div>

                      {formData.isCompanyAccount && (
                        <div className="col-12 col-sm-6 col-lg-4 mb-3 login-form-group">
                          <label htmlFor="companyName" className="form-label">
                            Account Owner Name{" "}
                            <span className="star-required text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            id="companyName"
                            name="companyName"
                            placeholder="Enter account owner name"
                            value={formData.companyName}
                            onChange={handleChange}
                            className={`form-control ${
                              errors.companyName ? "is_invalid" : ""
                            }`}
                          />
                          {errors.companyName && (
                            <div className="error-message">
                              {errors.companyName}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="col-12">
                        <div className="form__btn--group row d-flex justify-content-center mt-3">
                          <div className="col-auto ps-1">
                            <button
                              type="submit"
                              className="btn secondary-btn register-btn"
                              disabled={isLoading}
                            >
                              {isLoading && <Spinner />} Signup
                            </button>

                          </div>
                        </div>
                      </div>
                      <div className="signup-text-wrapper text-center mb-2">
                          <p>Already have an Account? <span className="signup-link cursor-pointer" onClick={redirectToLogin}>Login</span></p>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="login-footer-wrapper">
          <LoginFooter />
        </div>
        {isShowSignupSuccessPopup && (
          <SignupSuccessPopup
            onBrowseMarketPlace={redirectToMarketPlace}
            onCancel={redirectToMarketPlace}
            type={popupType}
          />
        )}
      </div>
    </>
  );
};

export default Signup;
